<template>
  <v-container class="map-fields-list pa-1">
    <div class="map-fields-list__subtitle">
      <p class="mb-1">
        {{ tkey('subtitle') }}
      </p>

      <BaseDownloadLink :url="downloadFileUrl">
        <v-btn size="small" variant="text" color="primary">
          {{ tkey('uploadedFile') }}
        </v-btn>
      </BaseDownloadLink>

      <p class="mt-1 mb-0">
        {{ tkey('autoMappedFields', { numMappedFields: automaticMappings.length }) }}:
        {{ automaticMappings.join(', ') }}
      </p>
    </div>

    <v-divider />

    <v-list density="compact" class="mt-2">
      <v-list-item class="divider">
        <v-row no-gutters>
          <v-col cols="6" class="pl-0 pb-1 font-weight-bold">
            {{ tkey('expected') }}
          </v-col>
          <v-col cols="6" class="pl-0 pb-1 font-weight-bold">
            {{ tkey('csv') }}
          </v-col>
        </v-row>
      </v-list-item>

      <v-list-item
        v-for="header in unmappedExpectedFields"
        :key="header.mongoField"
        density="compact"
      >
        <v-row no-gutters align="center">
          <v-col cols="6" class="pa-1 pl-0">
            {{ normalizeTranslation(header.translationKey, $t) }}
          </v-col>

          <v-col cols="6" class="pa-1 pl-0">
            <v-select
              :items="unmappedFields"
              :placeholder="tkey('selectField')"
              clearable
              hide-details
              density="compact"
              @update:model-value="updateMapping(header.mongoField, $event)"
            />
          </v-col>
        </v-row>
      </v-list-item>
    </v-list>

    <v-card-actions>
      <v-spacer />
      <v-btn variant="text" @click="cancel">
        {{ $t('actions.cancel') }}
      </v-btn>
      <v-btn :disabled="confirmDisabled" size="small" primary @click="confirmMappings">
        {{ $t('actions.confirm') }}
      </v-btn>
    </v-card-actions>
  </v-container>
</template>

<script>
import { find, sortBy } from 'lodash';
import { useInternationalization } from '@/composables/use-internationalization';

export default {
  props: {
    uploadedDataDetails: {
      type: Object,
      required: false,
      default: () => null,
    },

    downloadFileUrl: {
      type: String,
      required: false,
      default: '',
    },
  },

  setup() {
    const { normalizeTranslation } = useInternationalization();
    const { tkey } = useInternationalization('mapFields');

    return {
      normalizeTranslation,
      tkey,
    };
  },

  data() {
    return { mappings: [] };
  },

  computed: {
    automaticMappings() {
      return this.uploadedDataDetails.mappings.map(({ translationKey }) =>
        this.normalizeTranslation(translationKey, this.$t)
      );
    },

    unmappedFields() {
      return this.uploadedDataDetails
        ? this.uploadedDataDetails.unmappedFields.map(({ selectedField }) => selectedField)
        : [];
    },

    unmappedExpectedFields() {
      return this.uploadedDataDetails
        ? sortBy(this.uploadedDataDetails.unmappedExpectedFields, 'required').reverse()
        : [];
    },

    expectedFieldsMapped() {
      return new Set(this.mappings.map(({ mongoField }) => mongoField));
    },

    availableExpectedFields() {
      return this.unmappedExpectedFields.filter(
        ({ mongoField, required }) => required && !this.expectedFieldsMapped.has(mongoField)
      );
    },

    confirmDisabled() {
      return this.availableExpectedFields.length > 0;
    },
  },

  methods: {
    updateMapping(mongoField, selectedField) {
      // If no fields selected it means it was cleared and we must remove mapping
      if (!selectedField) return this.removeMapping(mongoField);

      const existingMapping = find(this.mappings, { mongoField });
      if (existingMapping) existingMapping.selectedField = selectedField;
      else this.mappings.push({ mongoField, selectedField });
    },

    removeMapping(clearedField) {
      this.mappings = this.mappings.filter(({ mongoField }) => mongoField !== clearedField);
    },

    confirmMappings() {
      this.$emit('confirm', this.mappings);
    },

    cancel() {
      this.$emit('cancel');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.map-fields-list {
  font-size: 12px;

  &__subtitle {
    padding: 8px 12px 12px;
  }

  .v-list {
    padding: 0;
  }

  .v-list-item {
    padding-left: 12px;
    padding-right: 0;
    min-height: auto;
    height: 36px;

    :deep(.v-list-item__content) {
      padding: 0;
    }

    &:nth-child(2n + 2) {
      background: $assortment-list-item-bg-colour !important;

      :deep(.v-input .v-input__slot),
      :deep(.v-input .v-select__slot) {
        background: $assortment-input-background !important;
      }
    }

    &__title {
      font-size: 12px;
      line-height: 20px;
    }
  }

  .divider {
    border-bottom: 1px solid $assortment-menu-border-colour;
  }
}
</style>
